import { BlobServiceClient, AnonymousCredential } from '@azure/storage-blob';
import type { TokenCredential } from '@azure/core-auth';
import {
  DEFAULT_SITE_MODE,
  withContentDefaults,
  type SiteContent,
  type SiteMode,
  type MediaItem,
  type PortalSettings,
  type FormSubmission,
  type NotificationSettings,
  type SubmissionStatus,
} from '@denise-augusta/domain';
import type {
  SiteContentStore,
  MediaStore,
  PortalSettingsStore,
  SubmissionsStore,
} from '@denise-augusta/capabilities';
import { environment } from '@/config/environment';
import { getStorageToken } from '@/auth/msal';

/**
 * Azure Blob-backed stores for the lattice capabilities.
 *
 * Local dev (Azurite):  endpoint + SAS query string, anonymous credential.
 * Test/live:            the signed-in admin's Entra ID token (RBAC —
 *                       Storage Blob Data Contributor on the account).
 */
function createBlobService(): BlobServiceClient {
  const { blobEndpoint, sas } = environment.storage;
  if (sas) {
    const query = sas.startsWith('?') ? sas : `?${sas}`;
    return new BlobServiceClient(`${blobEndpoint}${query}`, new AnonymousCredential());
  }
  const credential: TokenCredential = {
    getToken: async () => getStorageToken(),
  };
  return new BlobServiceClient(blobEndpoint, credential);
}

const CONTENT_BLOB = 'content.json';
const DRAFT_BLOB = 'content-draft.json';
const MODE_BLOB = 'site-mode.json';

export function createAzureSiteContentStore(): SiteContentStore {
  return {
    async load() {
      const container = createBlobService().getContainerClient(
        environment.storage.contentContainer
      );
      const blob = container.getBlockBlobClient(CONTENT_BLOB);
      try {
        const response = await blob.download();
        const body = await response.blobBody;
        if (!body) return null;
        return JSON.parse(await body.text()) as SiteContent;
      } catch (err: unknown) {
        if (isNotFound(err)) return null;
        throw err;
      }
    },

    async save(content: SiteContent) {
      const container = createBlobService().getContainerClient(
        environment.storage.contentContainer
      );
      const blob = container.getBlockBlobClient(CONTENT_BLOB);
      const data = JSON.stringify(content, null, 2);
      await blob.upload(data, new Blob([data]).size, {
        blobHTTPHeaders: { blobContentType: 'application/json' },
      });
    },

    async loadDraft() {
      const container = createBlobService().getContainerClient(
        environment.storage.contentContainer
      );
      try {
        const response = await container.getBlockBlobClient(DRAFT_BLOB).download();
        const body = await response.blobBody;
        if (!body) return null;
        return JSON.parse(await body.text()) as SiteContent;
      } catch (err: unknown) {
        if (isNotFound(err)) return null;
        throw err;
      }
    },

    async saveDraft(content: SiteContent) {
      const container = createBlobService().getContainerClient(
        environment.storage.contentContainer
      );
      const data = JSON.stringify(content, null, 2);
      await container.getBlockBlobClient(DRAFT_BLOB).upload(data, new Blob([data]).size, {
        blobHTTPHeaders: { blobContentType: 'application/json' },
      });
    },

    async deleteDraft() {
      const container = createBlobService().getContainerClient(
        environment.storage.contentContainer
      );
      try {
        await container.deleteBlob(DRAFT_BLOB);
      } catch (err: unknown) {
        if (!isNotFound(err)) throw err;
      }
    },

    async loadMode() {
      const container = createBlobService().getContainerClient(
        environment.storage.contentContainer
      );
      try {
        const response = await container.getBlockBlobClient(MODE_BLOB).download();
        const body = await response.blobBody;
        if (!body) return null;
        const parsed = JSON.parse(await body.text()) as { mode?: SiteMode };
        return parsed.mode === 'live' ? 'live' : 'locked';
      } catch (err: unknown) {
        if (isNotFound(err)) return null;
        throw err;
      }
    },

    async saveMode(mode: SiteMode) {
      const container = createBlobService().getContainerClient(
        environment.storage.contentContainer
      );
      const data = JSON.stringify({ mode, updatedAt: new Date().toISOString() });
      await container.getBlockBlobClient(MODE_BLOB).upload(data, new Blob([data]).size, {
        blobHTTPHeaders: { blobContentType: 'application/json' },
      });
    },
  };
}

export function createAzureMediaStore(): MediaStore {
  const mediaUrl = (name: string): string => {
    const { blobEndpoint, sas, mediaContainer } = environment.storage;
    const query = sas ? (sas.startsWith('?') ? sas : `?${sas}`) : '';
    return `${blobEndpoint}/${mediaContainer}/${encodeURIComponent(name)}${query}`;
  };

  return {
    async list(): Promise<MediaItem[]> {
      const container = createBlobService().getContainerClient(environment.storage.mediaContainer);
      const items: MediaItem[] = [];
      for await (const blob of container.listBlobsFlat()) {
        items.push({
          name: blob.name,
          url: mediaUrl(blob.name),
          contentType: blob.properties.contentType,
          sizeBytes: blob.properties.contentLength,
          lastModified: blob.properties.lastModified?.toISOString(),
        });
      }
      return items;
    },

    async upload(name: string, contentType: string, dataBase64: string): Promise<MediaItem> {
      const container = createBlobService().getContainerClient(environment.storage.mediaContainer);
      const blob = container.getBlockBlobClient(name);
      const bytes = Uint8Array.from(atob(dataBase64), (c) => c.charCodeAt(0));
      await blob.uploadData(bytes, {
        blobHTTPHeaders: { blobContentType: contentType },
      });
      return { name, url: mediaUrl(name), contentType, sizeBytes: bytes.byteLength };
    },

    async remove(name: string): Promise<void> {
      const container = createBlobService().getContainerClient(environment.storage.mediaContainer);
      await container.deleteBlob(name);
    },
  };
}

/**
 * Public, unauthenticated fetch of the published content document — used by
 * the public pages (the container allows anonymous blob read). Pass
 * kind='draft' for the draft-preview mode.
 */
export async function fetchPublishedContent(
  kind: 'published' | 'draft' = 'published'
): Promise<SiteContent | null> {
  const { blobEndpoint, sas, contentContainer } = environment.storage;
  if (!blobEndpoint) return null;
  const blob = kind === 'draft' ? DRAFT_BLOB : CONTENT_BLOB;
  const query = sas ? (sas.startsWith('?') ? sas : `?${sas}`) : '';
  try {
    const response = await fetch(`${blobEndpoint}/${contentContainer}/${blob}${query}`, {
      cache: 'no-store',
    });
    if (!response.ok) return null;
    return withContentDefaults((await response.json()) as Partial<SiteContent>);
  } catch {
    return null;
  }
}

const SETTINGS_BLOB = 'portal-settings.json';

/**
 * Portal settings live in the PRIVATE `portal` container (no public read —
 * it holds the LLM API key). Only authenticated portal users can reach it.
 */
export function createAzurePortalSettingsStore(): PortalSettingsStore {
  return {
    async load() {
      const container = createBlobService().getContainerClient(
        environment.storage.portalContainer
      );
      try {
        const response = await container.getBlockBlobClient(SETTINGS_BLOB).download();
        const body = await response.blobBody;
        if (!body) return null;
        return JSON.parse(await body.text()) as PortalSettings;
      } catch (err: unknown) {
        if (isNotFound(err)) return null;
        throw err;
      }
    },

    async save(settings: PortalSettings) {
      const container = createBlobService().getContainerClient(
        environment.storage.portalContainer
      );
      const data = JSON.stringify(settings, null, 2);
      await container.getBlockBlobClient(SETTINGS_BLOB).upload(data, new Blob([data]).size, {
        blobHTTPHeaders: { blobContentType: 'application/json' },
      });
    },
  };
}

const NOTIFICATION_SETTINGS_BLOB = 'notification-settings.json';
const SUBMISSION_PREFIX = 'inquiries/';
/**
 * How many inquiries the inbox loads. Each one is a separate blob fetch, so
 * this bounds the fan-out; blob names begin with the timestamp, so taking
 * the tail of the sorted list is the most recent.
 */
const SUBMISSION_PAGE_SIZE = 400;

/**
 * Contact-form inquiries live in the PRIVATE `submissions` container. The
 * /api/contact function writes them with a narrowly-scoped SAS; the portal
 * reads and triages them here with the signed-in admin's Entra ID token.
 *
 * Triage status is blob metadata rather than a field in the document, so
 * marking an inquiry read never rewrites what the visitor actually sent.
 */
export function createAzureSubmissionsStore(): SubmissionsStore {
  const containerClient = () =>
    createBlobService().getContainerClient(environment.storage.submissionsContainer);

  return {
    async list(): Promise<FormSubmission[]> {
      const container = containerClient();
      const paths: Array<{ path: string; status: SubmissionStatus }> = [];
      try {
        for await (const blob of container.listBlobsFlat({
          prefix: SUBMISSION_PREFIX,
          includeMetadata: true,
        })) {
          const status = blob.metadata?.status;
          paths.push({
            path: blob.name,
            status: status === 'read' || status === 'archived' ? status : 'new',
          });
        }
      } catch (err: unknown) {
        // No inquiries have ever been submitted, so the container is empty
        // or absent — an empty inbox, not an error.
        if (isNotFound(err)) return [];
        throw err;
      }

      const recent = paths.sort((a, b) => a.path.localeCompare(b.path)).slice(-SUBMISSION_PAGE_SIZE);
      const submissions = await Promise.all(
        recent.map(async ({ path, status }) => {
          try {
            const response = await container.getBlockBlobClient(path).download();
            const body = await response.blobBody;
            if (!body) return null;
            const parsed = JSON.parse(await body.text()) as FormSubmission;
            return { ...parsed, path, status };
          } catch (err: unknown) {
            if (isNotFound(err)) return null;
            throw err;
          }
        })
      );
      return submissions.filter((item): item is FormSubmission => item !== null);
    },

    async setStatus(path: string, status: SubmissionStatus) {
      await containerClient().getBlockBlobClient(path).setMetadata({ status });
    },

    async remove(path: string) {
      await containerClient().deleteBlob(path);
    },

    async loadNotificationSettings() {
      try {
        const response = await containerClient()
          .getBlockBlobClient(NOTIFICATION_SETTINGS_BLOB)
          .download();
        const body = await response.blobBody;
        if (!body) return null;
        return JSON.parse(await body.text()) as NotificationSettings;
      } catch (err: unknown) {
        if (isNotFound(err)) return null;
        throw err;
      }
    },

    async saveNotificationSettings(settings: NotificationSettings) {
      const data = JSON.stringify(settings, null, 2);
      await containerClient()
        .getBlockBlobClient(NOTIFICATION_SETTINGS_BLOB)
        .upload(data, new Blob([data]).size, {
          blobHTTPHeaders: { blobContentType: 'application/json' },
        });
    },
  };
}

/**
 * Public, unauthenticated fetch of the site access mode. Fails CLOSED: if the
 * mode blob is missing, storage is unconfigured, or the request errors, the
 * site is treated as locked (the default).
 */
export async function fetchSiteMode(): Promise<SiteMode> {
  const { blobEndpoint, sas, contentContainer } = environment.storage;
  if (!blobEndpoint) return DEFAULT_SITE_MODE;
  const query = sas ? (sas.startsWith('?') ? sas : `?${sas}`) : '';
  try {
    const response = await fetch(`${blobEndpoint}/${contentContainer}/${MODE_BLOB}${query}`, {
      cache: 'no-store',
    });
    if (!response.ok) return DEFAULT_SITE_MODE;
    const parsed = (await response.json()) as { mode?: SiteMode };
    return parsed.mode === 'live' ? 'live' : 'locked';
  } catch {
    return DEFAULT_SITE_MODE;
  }
}

function isNotFound(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'statusCode' in err &&
    (err as { statusCode?: number }).statusCode === 404
  );
}
